import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import logoZra from '../../media/logo_zra.png';

const MIN_LENGTH = 12;

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(Boolean(token));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing invitation link. Ask an administrator to resend your invite.');
      setPreviewLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await authAPI.previewInvite(token);
        if (cancelled) return;
        setPreview(data);
        if (!data?.valid) {
          setError('This invitation link is invalid or has expired. Ask an administrator to resend it.');
        }
      } catch {
        if (!cancelled) {
          setPreview({ valid: false });
          setError('Unable to validate invitation. Try again later.');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!token) {
      setError('Missing invite token.');
      return;
    }
    if (newPassword.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const result = await authAPI.acceptInvite(token, newPassword);
      setSuccess(true);
      setMessage(result?.message || 'Invitation accepted. You can now sign in.');
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      const data = err.response?.data;
      const apiMessage = typeof data?.message === 'string' ? data.message : null;
      const isTimeout = err.code === 'ECONNABORTED' || String(err.message || '').includes('timeout');
      const isNetwork = !err.response && err.request;
      if (apiMessage) {
        setError(apiMessage);
      } else if (isTimeout) {
        setError('The request timed out. Please try again.');
      } else if (isNetwork) {
        setError('Could not reach the server. Check your connection and try again.');
      } else {
        setError('Could not accept invitation. The link may have expired — ask an administrator to resend it.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (previewLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg text-center text-sm text-gray-600">
          Validating invitation…
        </div>
      </div>
    );
  }

  if (!token || preview?.valid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg space-y-4">
          <div className="flex justify-center">
            <img src={logoZra} alt="ZRA Logo" className="h-24 w-auto" />
          </div>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            <p className="text-sm">{error || 'Invalid invitation.'}</p>
          </div>
          <p className="text-center">
            <Link to="/login" className="text-primary-600 hover:text-primary-500 text-sm">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg">
        <div>
          <div className="flex justify-center">
            <img src={logoZra} alt="ZRA Logo" className="h-24 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Accept invitation
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Welcome{preview?.fullName ? `, ${preview.fullName}` : ''}. Set a password for{' '}
            <span className="font-medium text-gray-800">{preview?.username}</span>
            {preview?.email ? (
              <>
                {' '}
                ({preview.email})
              </>
            ) : null}
            .
          </p>
        </div>
        {success ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
              <p className="text-sm">{message}</p>
            </div>
            <p className="text-center text-sm text-gray-600">Redirecting to sign in…</p>
            <p className="text-center">
              <Link to="/login" className="text-sm text-primary-600 hover:text-primary-500">
                Sign in now
              </Link>
            </p>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <p className="text-sm">{error}</p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="sr-only">
                  Password
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_LENGTH}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder={`Password (min ${MIN_LENGTH} characters)`}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="sr-only">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_LENGTH}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Activating…' : 'Set password and continue'}
              </button>
              <p className="text-center">
                <Link to="/login" className="text-sm text-primary-600 hover:text-primary-500">
                  Back to sign in
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
